import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const ContentProduction = () => {
  const [activeDay, setActiveDay] = useState('Monday');
  const [activeTab, setActiveTab] = useState('hooks');

  const weeklySchedule = {
    Monday: { theme: 'Educational', icon: 'BookOpen', desc: 'Teach the audience something new about the niche.', color: 'from-blue-500 to-purple-600' },
    Tuesday: { theme: 'Value/Insight', icon: 'Lightbulb', desc: 'Share a deep industry insight or contrarian view.', color: 'from-yellow-500 to-orange-600' },
    Wednesday: { theme: 'Carousel', icon: 'Layers', desc: 'High-engagement swipeable content allowing depth.', color: 'from-pink-500 to-rose-600' },
    Thursday: { theme: 'Community/BTS', icon: 'Users', desc: 'Show the human side or highlight user generated content.', color: 'from-green-500 to-teal-600' },
    Friday: { theme: 'CTA/Promo', icon: 'ShoppingBag', desc: 'Direct call to action for services or products.', color: 'from-red-500 to-pink-600' },
    Saturday: { theme: 'Evergreen', icon: 'RefreshCw', desc: 'Content that remains relevant over long periods.', color: 'from-indigo-500 to-blue-600' },
    Sunday: { theme: 'Inspirational', icon: 'Sun', desc: 'Motivational content to prepare for the week ahead.', color: 'from-purple-500 to-indigo-600' },
  };

  const templates = {
    hooks: [
      "Here's what nobody tells you about...",
      "3 things I wish I knew before...",
      "Stop doing this if you want to...",
      "The truth about [Topic]..."
    ],
    captions: [
      "Structure: Hook + Context + Value + CTA",
      "Problem + Solution + Social Proof + Offer",
      "Behind Scenes + Process + Result + Question"
    ],
    tiktok: [
      "0-3s: Visual Hook",
      "3-13s: Agitate Problem",
      "13-28s: Present Solution",
      "28-30s: Call to Action"
    ]
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="max-w-7xl mx-auto"
    >
      {/* Enhanced header */}
      <motion.div 
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="text-center mb-12"
      >
        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-brown via-[#8B6B4D] to-apache bg-clip-text text-transparent">
          Content Production
        </h1>
        <p className="text-xl text-brown/70 max-w-3xl mx-auto">
          Systematic approach to creating engaging, platform-specific content.
        </p>
      </motion.div>

      {/* Enhanced Weekly Rhythm */}
      <motion.div 
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="glass-card rounded-3xl p-8 mb-12"
      >
        <h2 className="text-2xl font-bold text-brown mb-8">Weekly Posting Rhythm</h2>
        
        {/* Enhanced day selector */}
        <div className="flex flex-wrap gap-3 mb-8">
          {Object.keys(weeklySchedule).map((day, index) => (
            <motion.button
              key={day}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setActiveDay(day)}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className={`relative px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                activeDay === day 
                ? 'gradient-gold text-white shadow-lg' 
                : 'glass-card text-brown/70 hover:text-brown'
              }`}
            >
              {day}
              {activeDay === day && (
                <motion.div
                  layoutId="activeDay"
                  className="absolute inset-0 bg-gradient-to-r from-gold/20 to-apache/20 rounded-2xl -z-10"
                />
              )}
            </motion.button>
          ))}
        </div>
        
        <AnimatePresence mode="wait">
          <motion.div
            key={activeDay}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="glass-card rounded-2xl p-8 border border-white/30"
          >
            <div className="flex items-center space-x-6">
              <motion.div 
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className={`w-20 h-20 rounded-3xl flex items-center justify-center bg-gradient-to-br ${weeklySchedule[activeDay].color} shadow-lg`}
              >
                <SafeIcon name={weeklySchedule[activeDay].icon} className="w-10 h-10 text-white" />
              </motion.div>
              <div>
                <motion.h3 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-3xl font-bold text-brown mb-3"
                >
                  {weeklySchedule[activeDay].theme}
                </motion.h3>
                <motion.p 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-brown/70 text-lg"
                >
                  {weeklySchedule[activeDay].desc}
                </motion.p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Enhanced content frameworks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Posting Volume */}
        <motion.div 
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="glass-card rounded-3xl p-8"
        >
          <h2 className="text-2xl font-bold text-brown mb-8">Posting Volume & Framework</h2>
          <div className="space-y-4">
            {[
              { name: 'X / Twitter', icon: 'Twitter', posts: '1-2 posts / week', color: 'from-black to-gray-800' },
              { name: 'IG / Facebook', icon: 'Instagram', posts: '2-3 posts / week', color: 'from-purple-600 to-pink-600' },
              { name: 'TikTok', icon: 'Video', posts: '2 posts / week', color: 'from-black to-gray-900' }
            ].map((platform, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                whileHover={{ x: 8, scale: 1.02 }}
                className="glass-card p-6 rounded-2xl cursor-pointer group"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${platform.color} flex items-center justify-center mr-4`}
                    >
                      <SafeIcon name={platform.icon} className="w-6 h-6 text-white" />
                    </motion.div>
                    <span className="font-bold text-brown text-lg">{platform.name}</span>
                  </div>
                  <motion.span 
                    whileHover={{ scale: 1.1 }}
                    className="font-bold text-gold bg-gold/10 px-4 py-2 rounded-xl"
                  >
                    {platform.posts}
                  </motion.span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Content Templates */}
        <motion.div 
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="glass-card rounded-3xl p-8"
        >
          <h2 className="text-2xl font-bold text-brown mb-8">Content Templates</h2>
          
          {/* Enhanced tabs */}
          <div className="flex border-b border-white/20 mb-8">
            {['hooks', 'captions', 'tiktok'].map((tab, i) => (
              <motion.button
                key={tab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + i * 0.1 }}
                onClick={() => setActiveTab(tab)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
                className={`pb-4 px-6 text-sm font-semibold capitalize transition-all relative ${
                  activeTab === tab 
                  ? 'text-gold' 
                  : 'text-brown/50 hover:text-brown'
                }`}
              >
                {tab}
                {activeTab === tab && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-gold to-apache rounded-full"
                  />
                )}
              </motion.button>
            ))}
          </div>
          
          <div className="space-y-4">
            {templates[activeTab].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + i * 0.1 }}
                whileHover={{ x: 8, scale: 1.02 }}
                className="glass-card p-4 rounded-xl cursor-pointer group"
              >
                <div className="flex items-center">
                  <motion.div
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                    className="w-2 h-2 bg-gold rounded-full mr-3"
                  ></motion.div>
                  <span className="text-brown font-medium group-hover:text-brown/80 transition-colors">
                    {item}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ContentProduction;